import React, { useEffect } from 'react';
import { Container, Box, Typography, Card, CardContent, CardActionArea, Chip } from '@mui/material';
import { School, AdminPanelSettings, Create, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BRAND_GRADIENT = 'linear-gradient(to right, #00CCCC, #FFC840, #FFB000, #CD00FF, #DD50FF, #00B2AD)';

const ROLES = [
  {
    key: 'learner',
    label: 'Learner',
    description: 'Access your courses, track progress, and earn certificates.',
    icon: <School sx={{ fontSize: 40 }} />,
    color: '#1976d2',
    bg: '#E3F2FD',
    url: '/login/learner',
  },
  {
    key: 'admin',
    label: 'Admin',
    description: 'Manage users, monitor analytics, and oversee the platform.',
    icon: <AdminPanelSettings sx={{ fontSize: 40 }} />,
    color: '#7b1fa2',
    bg: '#F3E5F5',
    url: '/login/admin',
  },
  {
    key: 'content-manager',
    label: 'Content Manager',
    description: 'Create and publish courses, modules, and assessments.',
    icon: <Create sx={{ fontSize: 40 }} />,
    color: '#2e7d32',
    bg: '#E8F5E9',
    url: '/login/content-manager',
  },
];

const LoginLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Brand */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="h3" fontWeight={900}
            sx={{ background: BRAND_GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', textAlign: 'center' }}>
            Sedstart
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" mb={6} textAlign="center">
          Learning Management Platform — select your role to sign in
        </Typography>

        {/* Role cards */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
          {ROLES.map(role => (
            <Card key={role.key} elevation={2} sx={{
              width: { xs: '100%', sm: 240 },
              borderRadius: 3,
              border: '2px solid transparent',
              transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 8, borderColor: role.color },
            }}>
              <CardActionArea data-testid={`card-${role.key}`} onClick={() => navigate(role.url)} sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box sx={{
                    width: 72, height: 72, borderRadius: '50%', bgcolor: role.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 2, color: role.color,
                  }}>
                    {role.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: role.color }}>
                    {role.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                    {role.description}
                  </Typography>
                  <Chip
                    label="Sign in"
                    icon={<ArrowForward sx={{ fontSize: '14px !important' }} />}
                    sx={{ bgcolor: role.color, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        <Typography variant="caption" color="text.disabled" mt={6} textAlign="center">
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </Typography>
      </Box>
    </Container>
  );
};

export default LoginLandingPage;
